// pages/MotsPendingPage.jsx
// Page admin pour valider les mots en attente

import React, { useState, useEffect } from 'react';
import { Check, X, Edit2, Trash2, RefreshCw, Filter, CheckSquare } from 'lucide-react';
import './MotsPendingPage.css';

export default function MotsPendingPage() {
  const [motsPending, setMotsPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterLangue, setFilterLangue] = useState('');

  const langues = [
    { code: 'fr', label: 'Francais', flag: '🇫🇷' },
    { code: 'es', label: 'Espagnol', flag: '🇪🇸' },
    { code: 'en', label: 'Anglais', flag: '🇬🇧' },
  ];

  const types = [
    { value: 'nom', label: 'Nom' },
    { value: 'adjectif', label: 'Adjectif' },
    { value: 'adverbe', label: 'Adverbe' },
    { value: 'pronom', label: 'Pronom' },
    { value: 'expression', label: 'Expression' },
    { value: 'verbe', label: 'Verbe' },
  ];

  // genres supprimé - enrichissement se fait dans Mots

  // Charger les mots en attente
  const fetchMotsPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filterLangue
        ? `/api/admin/mots-pending?langue=${filterLangue}`
        : '/api/admin/mots-pending';
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setMotsPending(data.motsPending);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger les statistiques
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/mots-pending/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  useEffect(() => {
    fetchMotsPending();
    fetchStats();
  }, [filterLangue]);

  // Approuver un mot
  const handleApprove = async (id) => {
    try {
      const response = await fetch(`/api/admin/mots-pending/${id}/approve`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        fetchMotsPending();
        fetchStats();
      } else {
        alert('Erreur: ' + data.error);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Rejeter un mot
  const handleReject = async (id) => {
    const reason = prompt('Raison du rejet (optionnel):');
    try {
      const response = await fetch(`/api/admin/mots-pending/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await response.json();
      if (data.success) {
        fetchMotsPending();
        fetchStats();
      } else {
        alert('Erreur: ' + data.error);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Supprimer un mot
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer definitivement ce mot en attente ?')) return;
    try {
      const response = await fetch(`/api/admin/mots-pending/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        fetchMotsPending();
        fetchStats();
      } else {
        alert('Erreur: ' + data.error);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Ouvrir l'edition
  const handleStartEdit = (mot) => {
    setEditingId(mot.id);
    setEditForm({
      motCorrige: mot.motCorrige || mot.motOriginal,
      type: mot.type || 'nom',
    });
  };

  // Sauvegarder l'edition
  const handleSaveEdit = async (id) => {
    try {
      const response = await fetch(`/api/admin/mots-pending/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const data = await response.json();
      if (data.success) {
        setEditingId(null);
        fetchMotsPending();
      } else {
        alert('Erreur: ' + data.error);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Selection multiple
  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === motsPending.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(motsPending.map(m => m.id));
    }
  };

  // Approuver en masse
  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Approuver ${selectedIds.length} mot(s) ?`)) return;

    try {
      const response = await fetch('/api/admin/mots-pending/approve-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`${data.results.approved} mot(s) approuve(s), ${data.results.skipped} ignore(s)`);
        setSelectedIds([]);
        fetchMotsPending();
        fetchStats();
      } else {
        alert('Erreur: ' + data.error);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const getLangueFlag = (code) => {
    const langue = langues.find(l => l.code === code);
    return langue ? langue.flag : code;
  };

  if (loading && motsPending.length === 0) {
    return (
      <div className="mots-pending-page">
        <h1>Mots en attente de validation</h1>
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="mots-pending-page">
      <div className="page-header">
        <h1>Mots en attente de validation</h1>
        <button className="btn-refresh" onClick={() => { fetchMotsPending(); fetchStats(); }}>
          <RefreshCw size={18} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="stats-bar">
          <div className="stat pending">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">En attente</span>
          </div>
          <div className="stat rejected">
            <span className="stat-value">{stats.rejected}</span>
            <span className="stat-label">Rejetes</span>
          </div>
          <div className="stat-langues">
            {langues.map(l => (
              <span key={l.code} className="langue-stat">
                {l.flag} {stats.byLangue[l.code] || 0}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Filtres et actions */}
      <div className="toolbar">
        <div className="filters">
          <Filter size={18} />
          <select
            value={filterLangue}
            onChange={(e) => setFilterLangue(e.target.value)}
          >
            <option value="">Toutes les langues</option>
            {langues.map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
            ))}
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedIds.length} selectionne(s)</span>
            <button className="btn-bulk-approve" onClick={handleBulkApprove}>
              <CheckSquare size={16} />
              Approuver la selection
            </button>
          </div>
        )}
      </div>

      {/* Liste des mots */}
      {motsPending.length === 0 ? (
        <div className="empty-state">
          <p>Aucun mot en attente de validation</p>
        </div>
      ) : (
        <div className="mots-table">
          <div className="table-header">
            <div className="col-checkbox">
              <input
                type="checkbox"
                checked={selectedIds.length === motsPending.length && motsPending.length > 0}
                onChange={toggleSelectAll}
              />
            </div>
            <div className="col-langue">Langue</div>
            <div className="col-mot">Mot original</div>
            <div className="col-corrige">Mot corrige</div>
            <div className="col-type">Type</div>
            <div className="col-source">Source</div>
            <div className="col-actions">Actions</div>
          </div>

          {motsPending.map((mot) => (
            <div key={mot.id} className={`table-row ${selectedIds.includes(mot.id) ? 'selected' : ''}`}>
              <div className="col-checkbox">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(mot.id)}
                  onChange={() => toggleSelection(mot.id)}
                />
              </div>
              <div className="col-langue">{getLangueFlag(mot.langue)}</div>

              {editingId === mot.id ? (
                <>
                  <div className="col-mot">{mot.motOriginal}</div>
                  <div className="col-corrige">
                    <input
                      type="text"
                      value={editForm.motCorrige}
                      onChange={(e) => setEditForm({ ...editForm, motCorrige: e.target.value })}
                      autoCapitalize="off"
                    />
                  </div>
                  <div className="col-type">
                    <select
                      value={editForm.type}
                      onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    >
                      {types.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-source">-</div>
                  <div className="col-actions">
                    <button className="btn-save" onClick={() => handleSaveEdit(mot.id)} title="Sauvegarder">
                      <Check size={16} />
                    </button>
                    <button className="btn-cancel-edit" onClick={() => setEditingId(null)} title="Annuler">
                      <X size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-mot">{mot.motOriginal}</div>
                  <div className="col-corrige">
                    {mot.motCorrige && mot.motCorrige !== mot.motOriginal ? (
                      <span className="corrected">{mot.motCorrige}</span>
                    ) : (
                      <span className="no-correction">-</span>
                    )}
                  </div>
                  <div className="col-type">{mot.type || 'nom'}</div>
                  <div className="col-source">
                    {mot.Playlist ? (
                      <span className="playlist-name" title={mot.Playlist.nom}>
                        {mot.Playlist.nom.substring(0, 15)}{mot.Playlist.nom.length > 15 ? '...' : ''}
                      </span>
                    ) : '-'}
                  </div>
                  <div className="col-actions">
                    <button className="btn-approve" onClick={() => handleApprove(mot.id)} title="Approuver">
                      <Check size={16} />
                    </button>
                    <button className="btn-edit" onClick={() => handleStartEdit(mot)} title="Modifier">
                      <Edit2 size={16} />
                    </button>
                    <button className="btn-reject" onClick={() => handleReject(mot.id)} title="Rejeter">
                      <X size={16} />
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(mot.id)} title="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
